/** QuickPlan — Arborescence : sous-ensembles → pièces → pièces qu'elles portent.
 *  Clic = sélection (Ctrl = ajout), 👁 = masquer, double-clic = renommer.
 *  Glisser une pièce SUR une pièce = l'y attacher ; sur un sous-ensemble ou la racine =
 *  la détacher et l'y ranger. La position à l'écran est toujours conservée. */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import * as ops from '../../shared/ops.js';
import { descendants } from '../../shared/frames.js';

export default function StructurePanel() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  const select = useProjectStore((s) => s.select);
  const setSelection = useProjectStore((s) => s.setSelection);
  const mutate = useProjectStore((s) => s.mutate);
  const lookup = useProjectStore((s) => s.lookup)();
  const [filter, setFilter] = useState('');
  const [drag, setDrag] = useState(null);
  const [folded, setFolded] = useState({});
  const sel = new Set(selection);
  const f = filter.toLowerCase();
  const ids = new Set(project.instances.map((i) => i.id));
  const label = (i) => i.name || lookup(i.componentId)?.name || i.componentId;

  const kidsOf = (id) => project.instances.filter((i) => i.parentId === id);
  const matches = (i) => !f || `${label(i)} ${i.componentId}`.toLowerCase().includes(f)
    || kidsOf(i.id).some(matches);
  const childrenGroups = (pid) => project.groups.filter((g) => g.parentId === pid);
  // Têtes d'un sous-ensemble : pièces non portées (ou dont le porteur a disparu).
  const rootsIn = (gid) => project.instances.filter((i) => i.groupId === gid && !(i.parentId && ids.has(i.parentId)) && matches(i));

  const dragIds = () => (drag && sel.has(drag.id) ? selection : drag ? [drag.id] : []);

  const dropOnGroup = (targetGroupId) => {
    if (!drag) return;
    mutate((p, lk) => {
      if (drag.kind === 'group') return ops.setGroupParent(p, drag.id, targetGroupId);
      for (const id of ops.selectionRoots(p, dragIds())) {
        ops.setParent(p, lk, id, null, true);
        ops.updateInstance(p, lk, id, { groupId: targetGroupId });
      }
    });
    setDrag(null);
  };

  const dropOnInst = (targetId) => {
    if (!drag || drag.kind !== 'inst') return;
    mutate((p, lk) => {
      for (const id of ops.selectionRoots(p, dragIds())) if (id !== targetId) ops.setParent(p, lk, id, targetId, true);
    });
    setDrag(null);
  };

  const rename = (g) => {
    const name = window.prompt('Nom du sous-ensemble :', g.name);
    if (name?.trim()) mutate((p) => { p.groups.find((x) => x.id === g.id).name = name.trim().slice(0, 120); });
  };

  const selectGroup = (g, additive) => {
    const tree = ops.groupTree(project, g.id);
    const list = project.instances.filter((i) => tree.has(i.groupId)).map((i) => i.id);
    setSelection(additive ? [...new Set([...selection, ...list])] : list);
  };

  const renderGroup = (g, depth) => (
    <div key={g.id}>
      <div
        className={`tree-row group ${drag ? 'droppable' : ''}`}
        style={{ paddingLeft: 4 + depth * 14 }}
        draggable
        onDragStart={() => setDrag({ kind: 'group', id: g.id })}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.stopPropagation(); dropOnGroup(g.id); }}
        onClick={(e) => selectGroup(g, e.ctrlKey || e.metaKey)}
        onDoubleClick={() => rename(g)}
      >
        <button className="tw" onClick={(e) => { e.stopPropagation(); mutate((p) => { const x = p.groups.find((y) => y.id === g.id); x.collapsed = !x.collapsed; }); }}>{g.collapsed ? '▸' : '▾'}</button>
        <span className="grow">📁 {g.name}</span>
        <button className="tw" title="Masquer / afficher" onClick={(e) => { e.stopPropagation(); mutate((p) => { const x = p.groups.find((y) => y.id === g.id); x.hidden = !x.hidden; }); }}>{g.hidden ? '◌' : '👁'}</button>
        <button className="tw danger" title="Supprimer le sous-ensemble (son contenu remonte d'un niveau)" onClick={(e) => { e.stopPropagation(); mutate((p) => ops.deleteGroup(p, g.id)); }}>✕</button>
      </div>
      {!g.collapsed && (
        <>
          {childrenGroups(g.id).map((k) => renderGroup(k, depth + 1))}
          {rootsIn(g.id).map((i) => renderInst(i, depth + 1))}
        </>
      )}
    </div>
  );

  const renderInst = (i, depth) => {
    const comp = lookup(i.componentId);
    const kids = kidsOf(i.id).filter(matches);
    const isFolded = folded[i.id];
    const banned = drag?.kind === 'inst' && (drag.id === i.id || descendants(project, drag.id).has(i.id));
    return (
      <div key={i.id}>
        <div
          className={`tree-row ${sel.has(i.id) ? 'selected' : ''} ${i.hidden ? 'dim' : ''}`}
          style={{ paddingLeft: 4 + depth * 14 }}
          draggable
          onDragStart={(e) => { e.stopPropagation(); setDrag({ kind: 'inst', id: i.id }); }}
          onDragOver={(e) => { if (!banned) { e.preventDefault(); e.stopPropagation(); } }}
          onDrop={(e) => { e.stopPropagation(); dropOnInst(i.id); }}
          onClick={(e) => select(i.id, e.ctrlKey || e.metaKey || e.shiftKey)}
          title={comp ? `${comp.name}${i.step ? ` — étape ${i.step}` : ''}\nGlisser une pièce ici pour l'y attacher` : `Composant introuvable : ${i.componentId}`}
        >
          {kids.length
            ? <button className="tw" onClick={(e) => { e.stopPropagation(); setFolded({ ...folded, [i.id]: !isFolded }); }}>{isFolded ? '▸' : '▾'}</button>
            : <span className="tw-pad" />}
          <span className="swatch" style={{ background: i.color ?? comp?.color ?? '#844' }} />
          <span className="grow">{i.parentId && <span className="attach-mark" title="Attachée à sa pièce parente">↳ </span>}{label(i)}</span>
          {comp?.joint && <span className="step-badge" title="Articulée (double-clic dans la vue pour ouvrir)">{i.open ? '⟲ ouvert' : '⟲'}</span>}
          {i.step != null && <span className="step-badge">{i.step}</span>}
          <button className="tw" title="Masquer / afficher" onClick={(e) => { e.stopPropagation(); mutate((p) => { const x = p.instances.find((y) => y.id === i.id); x.hidden = !x.hidden; }); }}>{i.hidden ? '◌' : '👁'}</button>
        </div>
        {!isFolded && kids.map((k) => renderInst(k, depth + 1))}
      </div>
    );
  };

  return (
    <div className="panel-body">
      <div className="row">
        <input className="search grow" placeholder="Filtrer…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button title="Nouveau sous-ensemble (contenant la sélection)" onClick={() => mutate((p, lk) => {
          const g = ops.addGroup(p, 'Sous-ensemble');
          for (const id of ops.selectionRoots(p, selection)) ops.updateInstance(p, lk, id, { groupId: g.id });
        })}>+📁</button>
      </div>
      <div className="tree" onDragOver={(e) => e.preventDefault()} onDrop={() => dropOnGroup(null)}>
        {childrenGroups(null).map((g) => renderGroup(g, 0))}
        {rootsIn(null).map((i) => renderInst(i, 0))}
        {!project.instances.length && <div className="empty">Projet vide — posez un composant depuis la bibliothèque, insérez un assemblage type ou demandez à l'assistant.</div>}
      </div>
    </div>
  );
}
